"use client";

import * as React from "react";
import { Info } from "lucide-react";

import { Tooltip, type TooltipProps } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Props for the styled info hint icon.
 *
 * Hint intentionally stays thin: all interaction logic is delegated to Tooltip
 * through `interaction="toggle"`.
 */
type HintProps = {
  ariaLabel: string;
  content: Exclude<React.ReactNode, undefined>;
  testId?: string;
  className?: string;
  contentClassName?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  side?: TooltipProps["side"];
  align?: TooltipProps["align"];
};

/**
 * Styled info icon that opens a toggle-style tooltip.
 *
 * Use for explanatory metadata next to labels, metrics or short pieces of UI.
 */
function Hint({
  ariaLabel,
  content,
  testId,
  className,
  contentClassName,
  icon,
  disabled = false,
  side = "top",
  align = "center",
}: HintProps) {
  return (
    <Tooltip
      content={content}
      interaction="toggle"
      disabled={disabled}
      side={side}
      align={align}
      contentClassName={contentClassName}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        data-testid={testId}
        disabled={disabled}
        className={cn(
          "focus-visible:ring-ring/70 -m-1 inline-flex cursor-help touch-manipulation items-center justify-center rounded-full p-1.5 opacity-70 transition hover:opacity-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 sm:-m-1.5 sm:p-2 md:-m-1 md:p-1.5 lg:-m-1.5 lg:p-2",
          className,
        )}
      >
        {icon ?? (
          <Info
            aria-hidden="true"
            className="size-3 sm:size-3.5 md:size-3 lg:size-3.5"
          />
        )}
      </button>
    </Tooltip>
  );
}

export { Hint };
export type { HintProps };
